import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  createRateK1,
  updateRateK1,
  type RateK1Input,
  type RateK1Patch,
} from '@/api/tariffs'
import type { RateK1 } from '@/types/tariffs'
import { Button } from '@/components/ui/Button'
import { PillToggle } from '@/components/ui/PillToggle'
import { Input } from '@/components/ui/Input'
import { CompanyStatusBadge } from '@/components/companies/CompanyStatusBadge'
import { InlineNumberInput } from './InlineNumberInput'
import { InlineTextInput } from './InlineTextInput'

/**
 * §6.2 TARIFFS_SPEC.md — К1 (города). CRUD + деактивация вместо удаления.
 */
export function RatesK1Table({ rows }: { rows: RateK1[] }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [showInactive, setShowInactive] = useState(false)
  const [adding, setAdding] = useState(false)
  const [addCity, setAddCity] = useState('')
  const [addCoeff, setAddCoeff] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [rowError, setRowError] = useState<Record<string, string>>({})

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: RateK1Patch }) => updateRateK1(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['tariffs', 'rates-k1'] })
      const prev = qc.getQueryData<RateK1[]>(['tariffs', 'rates-k1'])
      if (prev) {
        qc.setQueryData<RateK1[]>(
          ['tariffs', 'rates-k1'],
          prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
        )
      }
      return { prev }
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['tariffs', 'rates-k1'], ctx.prev)
      setRowError((s) => ({
        ...s,
        [vars.id]: err instanceof Error ? err.message : t('tariffs.errorGeneric'),
      }))
    },
    onSuccess: (_d, vars) => {
      setRowError((s) => {
        if (!s[vars.id]) return s
        const next = { ...s }
        delete next[vars.id]
        return next
      })
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tariffs', 'rates-k1'] })
    },
  })

  const createMut = useMutation({
    mutationFn: (input: RateK1Input) => createRateK1(input),
    onSuccess: () => {
      setAdding(false)
      setAddCity('')
      setAddCoeff('')
      setAddError(null)
      void qc.invalidateQueries({ queryKey: ['tariffs', 'rates-k1'] })
    },
    onError: (err) => {
      setAddError(err instanceof Error ? err.message : t('tariffs.k1.addFailed'))
    },
  })

  const visible = rows
    .filter((r) => showInactive || r.is_active)
    .sort((a, b) => a.city.localeCompare(b.city, 'ru'))

  function submitAdd() {
    setAddError(null)
    const coeff = Number(addCoeff.replace(',', '.'))
    if (!addCity.trim()) {
      setAddError(t('tariffs.k1.errCity'))
      return
    }
    if (!Number.isFinite(coeff) || coeff <= 0) {
      setAddError(t('tariffs.k1.errCoeff'))
      return
    }
    createMut.mutate({ city: addCity.trim(), coefficient: coeff, is_active: true })
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PillToggle
          checked={showInactive}
          onChange={setShowInactive}
          label={t('tariffs.showInactive')}
        />
        <Button
          variant="primary"
          onClick={() => setAdding((v) => !v)}
          aria-expanded={adding}
        >
          {adding ? t('tariffs.hideForm') : t('tariffs.k1.addCity')}
        </Button>
      </div>

      {adding ? (
        <div className="rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-3">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
            <Input
              label={t('tariffs.k1.colCity')}
              value={addCity}
              onChange={(e) => setAddCity(e.target.value)}
              placeholder={t('tariffs.k1.cityPlaceholder')}
            />
            <Input
              label={t('tariffs.coefficient')}
              value={addCoeff}
              onChange={(e) => setAddCoeff(e.target.value)}
              placeholder="0.9"
              inputMode="decimal"
            />
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={submitAdd}
                disabled={createMut.isPending}
              >
                {createMut.isPending ? t('tariffs.adding') : t('tariffs.add')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setAdding(false)
                  setAddCity('')
                  setAddCoeff('')
                  setAddError(null)
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
          {addError ? (
            <p className="stryzh-input-error-text mt-2">{addError}</p>
          ) : null}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-[color:var(--stryzh-color-border-default)]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[color:var(--stryzh-color-surface)] text-[color:var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2">{t('tariffs.k1.colCity')}</th>
              <th className="px-3 py-2">{t('tariffs.coefficient')}</th>
              <th className="px-3 py-2">{t('tariffs.colStatus')}</th>
              <th className="px-3 py-2 text-right">{t('tariffs.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-6 text-center text-[color:var(--stryzh-color-text-secondary)]"
                >
                  {t('tariffs.noRecords')}
                </td>
              </tr>
            ) : (
              visible.map((r) => {
                const inactive = !r.is_active
                return (
                  <tr
                    key={r.id}
                    className={`border-t border-[color:var(--stryzh-color-border-default)] ${inactive ? 'opacity-60' : ''}`}
                  >
                    <td className="px-3 py-2">
                      <InlineTextInput
                        value={r.city}
                        ariaLabel={t('tariffs.k1.ariaCity', { city: r.city })}
                        width={200}
                        onCommit={(city) => updateMut.mutate({ id: r.id, patch: { city } })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <InlineNumberInput
                        value={r.coefficient}
                        ariaLabel={t('tariffs.k1.ariaCoeff', { city: r.city })}
                        width={96}
                        formatter={(n) => String(n)}
                        onCommit={(v) => {
                          if (v != null) updateMut.mutate({ id: r.id, patch: { coefficient: v } })
                        }}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <CompanyStatusBadge isActive={r.is_active} compact />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant={r.is_active ? 'secondary' : 'primary'}
                        onClick={() =>
                          updateMut.mutate({ id: r.id, patch: { is_active: !r.is_active } })
                        }
                      >
                        {r.is_active ? t('tariffs.deactivate') : t('tariffs.activate')}
                      </Button>
                      {rowError[r.id] ? (
                        <div className="stryzh-input-error-text mt-1">{rowError[r.id]}</div>
                      ) : null}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
